import { useEffect } from 'react'
import {
  normalizeOnboardingScope,
  validateScopedOnboardingRun,
  isTargetResponse,
  withTarget,
  type OnboardingAdvanceOptions,
  type OnboardingCsvRequest,
  type OnboardingEmailTemplate,
  type OnboardingEmailTemplateDraft,
  type OnboardingPreflight,
  type OnboardingRun,
  type OnboardingRunTargetInput,
  type OnboardingSettleOutcome,
  type OnboardingStepName,
} from './onboardingTypes'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { api, CostSummaryResponse, publishProject, unpublishProject, getPublicationStatus, getPublishedSnapshot, getPublicationVersions, getPublicationVersion, getPublicationLive, clonePublishedProject, getTemplates, PublicationStatus, PublishedSnapshot, VersionListItem, VersionDetail, LiveSnapshot, type FlagListItem, type ApiValue } from './client'
import { getStyle, getUserStyle, listStyles, listUserStyles, listFavorites, addFavorite, removeFavorite, favoriteKey, type StyleDetail, type StyleListResponse, type UserStyleListResponse, type StyleSummary, type FavoritesListResponse, type FavoriteRef } from './styles'
import { NO_STYLE_OPTION, VISUAL_STYLES } from '../constants/styles'
import { normalizeLocale } from '../i18n/supportedLocales'
import { useAuth } from '../contexts/AuthContext'
import { useAuthOptional } from '../contexts/authContextRaw'
import { DEFAULT_CANVAS_ID } from '../contexts/ActiveCanvasIdContext'
import { createDebug } from '../utils/debugLog'
import { reconcileProjectConfigRead, saveProjectConfig } from './projectConfigSaves'
import { subscribeProjectConfigChanged } from '../components/canvas-v2/projectSettingsEvents'
import { subscribeToGatewayConfig } from '../hooks/useVideoModels'
import type { Project, ProjectListItem, PaginatedResponse, CreateProjectRequest, ProjectStatus, ProjectConfig, UpdateProjectConfigRequest, DashboardTab, TabScope, TabSortBy, TabSortDirection, TemplateCatalogResponse } from './types'

const styleLog = createDebug('dw:style-library')

// Query keys
export const queryKeys = {
  projects: ['projects'],
  project: (id: string) => ['project', id],
  // Prefix only — invalidate with this to hit every user's cached balance.
  // The QUERY itself must use `sparksFor(userId)`: a bare ['sparks'] key is
  // shared across identities, so signing out and back in as someone else in
  // the same tab would serve the previous account's balance until refetch.
  sparks: ['sparks'],
  sparksFor: (userId: string | undefined) => ['sparks', userId ?? 'anonymous'],
  // Keyed by checkout mode: the placeholder and live catalogs carry different
  // item ids, and a cached placeholder id sent to the live endpoint is
  // rejected as UNKNOWN_ITEM. The flag can flip inside the cache window.
  billingCatalog: (mode: 'live' | 'placeholder') => ['billing', 'catalog', mode],
  pricing: ['pricing'],
  costSummary: (projectId: string) => ['costSummary', projectId],
  publicationStatus: (projectId: string) => ['publicationStatus', projectId],
  publishedSnapshot: (key: string) => ['publishedSnapshot', key],
  publicationVersions: (projectId: string) => ['publicationVersions', projectId],
  publicationVersion: (projectId: string, versionNumber: number) => ['publicationVersion', projectId, versionNumber],
  publicationLive: (projectId: string) => ['publicationLive', projectId],
  styles: (uiLocale: string) => ['styles', uiLocale],
  style: (id: string, uiLocale: string) => ['styles', id, uiLocale],
  userStyles: ['userStyles'],
  userStyle: (id: string) => ['userStyles', id],
  styleFavorites: ['styleFavorites'],
  // Kept for the invalidation broadcasts that do not know the scope: it is the
  // PREFIX of every scoped key below, so invalidating it clears all of them.
  dashboardTabs: ['dashboardTabs'],
  dashboardTabsScoped: (scope: TabScope) => [
    'dashboardTabs',
    scope.teamId ?? 'no-team',
    scope.visibility,
  ],
  dashboardTabProjects: (tabId: string, page: number) =>
    ['dashboardTabProjects', tabId, page],
  // Canvas-scoped: WorkflowState config (style / aspect ratio / …) is stored per
  // canvas, so the cache key MUST include the canvas id or switching canvases
  // would read/stamp another canvas's config.
  projectConfig: (projectId: string, canvasId: string = DEFAULT_CANVAS_ID) => [
    'projectConfig',
    projectId,
    canvasId,
  ],
  templates: (limit: number, offset: number) => ['templates', limit, offset],
  flags: ['adminFlags'],
  internalModelEntitlements: ['adminInternalModelEntitlements'],
  // Operator balance lookup, keyed by the email being inspected. Lowercased so
  // two spellings of the same address share one cache entry (the backend match
  // is already case-insensitive).
  operatorBalance: (email: string) => ['adminOperatorBalance', email.trim().toLowerCase()],
  operatorUserTeams: (email: string) => ['adminOperatorUserTeams', email.trim().toLowerCase()],
}

import { useActiveTeam } from '../contexts/ActiveTeamContext'
import { useClientFlags } from '../hooks/useClientFlags'
import { useTeamsEnabled } from '../features/teams/useTeamsEnabled'
import { asVisibility } from '../components/dashboard/VisibilitySwitch'

export const DASHBOARD_PAGE_SIZE = 10
export const LANDING_RECENT_PROJECT_LIMIT = 3
// The landing shelf has no switcher of its own, so it shows the same half the
// dashboard opens on. Kept as a named constant rather than a bare 'private' so
// the two cannot drift silently.
export const DEFAULT_LIST_VISIBILITY = 'private'
// The landing Recent shelf spans BOTH halves of the active team. "Recent"
// means "what you were last working on", and hiding work you just shared with
// your team is wrong on its face -- a published project vanished from the
// shelf, which is the bug this fixes. It is 'any' VISIBILITY, not any team:
// the request still carries `team_id`, so the shelf never reaches another
// team's projects.
export const RECENT_SHELF_VISIBILITY = 'any'

/**
 * The team argument for a project-list query, plus whether it may run yet.
 *
 * WHY A READINESS GATE. Both inputs to `teamId` settle asynchronously, and both
 * are in the query key, so a list hook that just reads them fires a REQUEST PER
 * SETTLEMENT. On /dashboard that was two GETs on every load:
 *
 *   1. flags snapshot not in yet -> `useTeamsEnabled()` returns its `false`
 *      default, `activeTeam` is still null  -> fetch with no team
 *   2. flags + teams resolve                -> fetch with the team
 *
 * The first request is not merely wasted: it is answered with the UNSCOPED
 * list, so the dashboard paints the wrong set and then corrects itself, which
 * reads as a flicker of other people's projects.
 *
 * Holding the query until both have settled makes it exactly one request, with
 * the team when the feature is on and without it when off. `isLoading` is
 * false once a query has data, so this gates the first load, not refetches.
 *
 * `teamId` is null whenever the feature is off — not merely absent from the
 * key. Sending an id while the backend gate is off would scope a list by a
 * feature that is supposed to be inert.
 */
function useProjectListTeamScope(): { teamId: string | null; ready: boolean } {
  const { isLoading: flagsLoading } = useClientFlags()
  const teamsEnabled = useTeamsEnabled()
  const { activeTeam, isLoading: teamsLoading } = useActiveTeam()

  // Teams off: nothing to wait for, and no team to send.
  if (!teamsEnabled) {
    return { teamId: null, ready: !flagsLoading }
  }
  return { teamId: activeTeam?.id ?? null, ready: !flagsLoading && !teamsLoading }
}

// Projects
export function useProjects(limit = 50, offset = 0) {
  // The active team is part of the QUERY KEY, not just the request. Without it
  // react-query serves the previous team's cached page on switch and the
  // dashboard appears not to change — the bug this fixes. Keying on it makes
  // each team its own cache entry, so switching back is instant rather than a
  // refetch.
  const { teamId, ready } = useProjectListTeamScope()
  return useQuery<PaginatedResponse<ProjectListItem>>({
    queryKey: [...queryKeys.projects, { limit, offset, teamId }],
    queryFn: () => api.listProjects(limit, offset, teamId),
    enabled: ready,
  })
}

// Sparks (credits balance)
const _isLocalDev = import.meta.env.DEV &&
  !import.meta.env.VITE_ENABLE_AUTH &&
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))

export function useSparks(projectId?: string) {
  const { isAuthenticated, user } = useAuth()
  return useQuery<{ balance: number; pool?: number | null; allowance_remaining?: number | null }>({
    // User-scoped: ['sparks'] alone survives a sign-out/sign-in in the same tab
    // and would briefly render the previous account's balance. Invalidations
    // still use the bare ['sparks'] prefix, which matches this partially.
    // The project is part of the KEY as well as the request: two projects can
    // resolve to DIFFERENT wallets (RFC-1372 C2), so a shared key would serve
    // one project's balance on another's workspace.
    queryKey: [...queryKeys.sparksFor(user?.id), projectId ?? null],
    queryFn: () => api.getSparks(projectId),
    enabled: isAuthenticated || _isLocalDev,
    staleTime: 30_000,
  })
}

// Billing catalog (@meta.com-gated server-side; the console additionally hides
// the surface behind `billing_sandbox_enabled`). Static data today, so a long
// staleTime — but still a query, because the real catalog will be dynamic.
export function useBillingCatalog(enabled: boolean, mode: 'live' | 'placeholder' = 'placeholder') {
  return useQuery({
    queryKey: queryKeys.billingCatalog(mode),
    queryFn: () => api.getBillingCatalog(),
    enabled,
    staleTime: 5 * 60_000,
  })
}

// Operator-only credit mint (T276405916): adds credits FOR a target user by
// email. Not a self-serve flow — gated server-side by the topup_operator
// permission. Does not invalidate the caller's own balance (the recipient is a
// different user) — only the looked-up balance for the recipient's email.
export function useOperatorTopup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      targetEmail,
      sparks,
      reason,
      targetTeamId,
    }: {
      targetEmail: string
      sparks: number
      reason?: string
      /** Credit this team's pooled wallet instead of the recipient's own. */
      targetTeamId?: string
    }) => {
      return api.operatorTopup(targetEmail, sparks, reason, targetTeamId)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.operatorBalance(variables.targetEmail),
      })
      // The team list carries balances, so a mint into a pool makes it stale
      // too. Invalidated unconditionally rather than only for a team mint: a
      // personal-wallet mint moves the personal team's balance as well, via
      // the C11 fallback that reads `user_spark_balances` for a personal team
      // with no wallet row. Skipping it there would leave the picker showing
      // the pre-mint number for the row the operator just credited.
      queryClient.invalidateQueries({
        queryKey: queryKeys.operatorUserTeams(variables.targetEmail),
      })
    },
  })
}

// Operator-only balance lookup for the target of a top-up / deduction.
//
// `enabled` is the caller's "this email is worth asking about" gate — the page
// passes a debounced, syntactically-valid address, so a half-typed one never
// reaches the server (and never logs a spurious USER_NOT_FOUND).
//
// `retry: false` is deliberate: the informative outcomes here are 404 (no such
// user) and 403 (not an operator), and retrying either just delays the answer.
// staleTime is 0 because the balance moves underneath us — the recipient can
// spend at any moment, and the operator is about to act on this number.
export function useOperatorBalance(email: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.operatorBalance(email),
    queryFn: () => api.operatorGetBalance(email.trim()),
    enabled,
    retry: false,
    staleTime: 0,
  })
}

// Operator-only: the wallets creditable for this user, with balances. Same
// `enabled` gate and `retry: false` reasoning as useOperatorBalance.
//
// staleTime is 0 for the same reason too — these are live pooled balances the
// operator is about to act on, and a cached one would be a number they believe
// and the mint disproves.
export function useOperatorUserTeams(email: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.operatorUserTeams(email),
    queryFn: () => api.operatorGetUserTeams(email.trim()),
    enabled,
    retry: false,
    staleTime: 0,
  })
}

// Operator-only credit deduction — the inverse of useOperatorTopup, gated by
// the same topup_operator permission. `sparks` is positive. Invalidates the
// looked-up balance for that email so the page reflects the new number without
// the operator re-typing.
export function useOperatorDeduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      targetEmail,
      sparks,
      reason,
    }: {
      targetEmail: string
      sparks: number
      reason?: string
    }) => {
      return api.operatorDeduct(targetEmail, sparks, reason)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.operatorBalance(variables.targetEmail),
      })
    },
  })
}

// RFC-0856 I7: feature-flag admin (operator-only, gated by AdminFlagsRoute +
// the backend flags_admin permission). The list re-reads on every successful
// mutation so version/value stay fresh — this is also the 409-conflict recovery
// path (invalidate → refetch → retry against the new version).
export function useFlags() {
  return useQuery<FlagListItem[]>({
    queryKey: queryKeys.flags,
    queryFn: () => api.listFlags(),
    staleTime: 10_000,
  })
}

export function useSetFlag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      key,
      value,
      expectedVersion,
      confirm,
    }: {
      key: string
      value: ApiValue
      expectedVersion: number
      confirm?: boolean
    }) => api.setFlag(key, value, expectedVersion, confirm),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.flags }),
  })
}

export function useRefreshFlags() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.refreshFlags(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.flags }),
  })
}

export function useInternalModelEntitlements() {
  return useQuery({
    queryKey: queryKeys.internalModelEntitlements,
    queryFn: () => api.listInternalModelEntitlements(),
    staleTime: 10_000,
    retry: false,
  })
}

export function useGrantInternalModelEntitlement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (email: string) => api.grantInternalModelEntitlement(email),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.internalModelEntitlements }),
  })
}

export function useRevokeInternalModelEntitlement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.revokeInternalModelEntitlement(userId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.internalModelEntitlements }),
  })
}

export function useCostSummary(projectId: string | undefined) {
  return useQuery<CostSummaryResponse>({
    queryKey: queryKeys.costSummary(projectId!),
    queryFn: () => api.getCostSummary(projectId!),
    enabled: !!projectId,
  })
}

export function useProject(projectId: string) {
  return useQuery<Project>({
    queryKey: queryKeys.project(projectId),
    queryFn: () => api.getProject(projectId),
    enabled: !!projectId,
    retry: false,
  })
}

// Create project
export function useCreateProject() {
  const queryClient = useQueryClient()
  // Stamped HERE rather than at each call site. Projects are created from the
  // landing hero, the dashboard, and the empty-tab CTA; making each one
  // remember the active team is how one of them silently keeps creating into
  // Personal. A caller that genuinely needs a different team can still pass
  // `team_id` explicitly and it wins.
  const { activeTeam } = useActiveTeam()
  const teamsEnabled = useTeamsEnabled()

  return useMutation({
    mutationFn: async (request: CreateProjectRequest) => {
      const teamId = teamsEnabled ? (activeTeam?.id ?? undefined) : undefined
      return api.createProject({ team_id: teamId, ...request })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
    },
  })
}

// Delete project
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (projectId: string) => {
      return api.deleteProject(projectId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

// Duplicate project (clone your OWN project into a new private copy).
// Mirrors useDeleteProject's invalidation so the new "(Copy)" card surfaces in
// the legacy `projects` list, the paginated `dashboardTabProjects` pages, and
// keeps the tab count badges (`dashboardTabs`) coherent.
export function useDuplicateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (projectId: string) => {
      return api.cloneProject(projectId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

// Star / unstar project
// Optimistic updates run across both query families: the legacy `projects`
// list and the new paginated `dashboardTabProjects` pages.
function _starOptimistic(starred: boolean) {
  const newStarredAt = starred ? new Date().toISOString() : null
  return (projectId: string) =>
    (queryClient: ReturnType<typeof useQueryClient>) => {
      const previousProjects = queryClient.getQueriesData<PaginatedResponse<ProjectListItem>>({
        queryKey: queryKeys.projects,
      })
      const previousTabProjects = queryClient.getQueriesData<PaginatedResponse<ProjectListItem>>({
        queryKey: ['dashboardTabProjects'],
      })
      const updater = (old?: PaginatedResponse<ProjectListItem>) => {
        if (!old) return old
        return {
          ...old,
          data: old.data.map((p) =>
            p.id === projectId ? { ...p, starred_at: newStarredAt } : p,
          ),
        }
      }
      queryClient.setQueriesData<PaginatedResponse<ProjectListItem>>(
        { queryKey: queryKeys.projects },
        updater,
      )
      queryClient.setQueriesData<PaginatedResponse<ProjectListItem>>(
        { queryKey: ['dashboardTabProjects'] },
        updater,
      )
      return { previousProjects, previousTabProjects }
    }
}

type StarContext = {
  previousProjects: ReturnType<ReturnType<typeof useQueryClient>['getQueriesData']>
  previousTabProjects: ReturnType<ReturnType<typeof useQueryClient>['getQueriesData']>
}

function _restoreStarContext(
  queryClient: ReturnType<typeof useQueryClient>,
  context: StarContext | undefined,
) {
  if (!context) return
  for (const [key, data] of context.previousProjects) queryClient.setQueryData(key, data)
  for (const [key, data] of context.previousTabProjects) queryClient.setQueryData(key, data)
}

function _invalidateStarFamilies(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.projects })
  queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
}

export function useStarProject() {
  const queryClient = useQueryClient()
  const apply = _starOptimistic(true)

  return useMutation({
    mutationFn: (projectId: string) => api.starProject(projectId),
    onMutate: async (projectId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projects })
      await queryClient.cancelQueries({ queryKey: ['dashboardTabProjects'] })
      return apply(projectId)(queryClient)
    },
    onError: (_err, _projectId, context) => _restoreStarContext(queryClient, context),
    onSettled: () => _invalidateStarFamilies(queryClient),
  })
}

export function useUnstarProject() {
  const queryClient = useQueryClient()
  const apply = _starOptimistic(false)

  return useMutation({
    mutationFn: (projectId: string) => api.unstarProject(projectId),
    onMutate: async (projectId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projects })
      await queryClient.cancelQueries({ queryKey: ['dashboardTabProjects'] })
      return apply(projectId)(queryClient)
    },
    onError: (_err, _projectId, context) => _restoreStarContext(queryClient, context),
    onSettled: () => _invalidateStarFamilies(queryClient),
  })
}

// Update project
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string
      data: { name?: string; status?: ProjectStatus; has_celebrity?: boolean }
    }) => {
      return api.updateProject(projectId, data)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.project(variables.projectId) })
      const previousProject = queryClient.getQueryData(queryKeys.project(variables.projectId))
      queryClient.setQueryData(queryKeys.project(variables.projectId), (old: Project | undefined) =>
        old ? { ...old, ...variables.data } : old
      )

      // T284464051: also patch the two list families the dashboard renders from.
      // `queryKeys.project(id)` is the single-project cache — the dashboard card
      // reads `projects` / `dashboardTabProjects` instead, so without this the
      // status badge would not move until the onSettled refetch landed. Mirrors
      // `_starOptimistic`, which had to solve the same problem.
      await queryClient.cancelQueries({ queryKey: queryKeys.projects })
      await queryClient.cancelQueries({ queryKey: ['dashboardTabProjects'] })
      const previousProjects = queryClient.getQueriesData<PaginatedResponse<ProjectListItem>>({
        queryKey: queryKeys.projects,
      })
      const previousTabProjects = queryClient.getQueriesData<PaginatedResponse<ProjectListItem>>({
        queryKey: ['dashboardTabProjects'],
      })
      // Only `name` and `status` exist on ProjectListItem; `has_celebrity` does
      // not, so spreading `variables.data` wholesale would inject a phantom key.
      const listPatch: Partial<ProjectListItem> = {}
      if (variables.data.name !== undefined) listPatch.name = variables.data.name
      if (variables.data.status !== undefined) listPatch.status = variables.data.status
      if (Object.keys(listPatch).length > 0) {
        const updater = (old?: PaginatedResponse<ProjectListItem>) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.map((p) =>
              p.id === variables.projectId ? { ...p, ...listPatch } : p,
            ),
          }
        }
        queryClient.setQueriesData<PaginatedResponse<ProjectListItem>>(
          { queryKey: queryKeys.projects },
          updater,
        )
        queryClient.setQueriesData<PaginatedResponse<ProjectListItem>>(
          { queryKey: ['dashboardTabProjects'] },
          updater,
        )
      }

      return { previousProject, previousProjects, previousTabProjects }
    },
    onError: (_err, variables, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(queryKeys.project(variables.projectId), context.previousProject)
      }
      for (const [key, data] of context?.previousProjects ?? []) {
        queryClient.setQueryData(key, data)
      }
      for (const [key, data] of context?.previousTabProjects ?? []) {
        queryClient.setQueryData(key, data)
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.project(variables.projectId) })
    },
  })
}

/**
 * Read the per-canvas workflow config (style / aspect ratio / production target).
 * Used by the canvas gear menu and by WorkspacePageV2 for new-node defaults.
 *
 * `canvasId` MUST be the active canvas — WorkflowState config is stored per
 * canvas, so a change made on canvas A must not be read from canvas B. Callers
 * mounted below `ActiveCanvasIdProvider` can pass `useActiveCanvasId()`; callers
 * above it (e.g. WorkspacePageV2, which mounts the provider) MUST pass their
 * local active-canvas id explicitly. Omitting it targets the default canvas.
 */
export function useProjectConfig(
  projectId: string | undefined,
  canvasId: string = DEFAULT_CANVAS_ID,
) {
  const queryClient = useQueryClient()
  // Backend `project_config_changed` (e.g. a Director `update_project_config`
  // call) → refetch so the gear and chat cards show the new values live.
  useEffect(() => {
    if (!projectId) return undefined
    return subscribeProjectConfigChanged((detail) => {
      if (detail.projectId !== projectId || detail.canvasId !== canvasId) return
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectConfig(projectId, canvasId) })
    })
  }, [projectId, canvasId, queryClient])
  // `effective_default_video_model` resolves through the user's default BYOM
  // model, so a gateway config save/delete must refresh it too.
  useEffect(() => {
    if (!projectId) return undefined
    return subscribeToGatewayConfig(() => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectConfig(projectId, canvasId) })
    })
  }, [projectId, canvasId, queryClient])
  return useQuery<ProjectConfig>({
    queryKey: queryKeys.projectConfig(projectId!, canvasId),
    queryFn: async () => reconcileProjectConfigRead(
      queryClient,
      queryKeys.projectConfig(projectId!, canvasId),
      await api.getProjectConfig(projectId!, canvasId),
    ),
    enabled: !!projectId,
  })
}

/** Save in click order per canvas, retaining optimistic edits until confirmed. */
export function useUpdateProjectConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      data,
      canvasId = DEFAULT_CANVAS_ID,
    }: {
      projectId: string
      data: UpdateProjectConfigRequest
      canvasId?: string
    }) => saveProjectConfig(
      queryClient,
      queryKeys.projectConfig(projectId, canvasId),
      projectId,
      canvasId,
      data,
      () => api.updateProjectConfig(projectId, data, canvasId),
    ),
  })
}

// ---------------------------------------------------------------------------
// Publication hooks (RFC-0295 — Snapshot-based sharing)
// ---------------------------------------------------------------------------

export function usePublicationStatus(projectId: string | undefined) {
  return useQuery<PublicationStatus>({
    queryKey: queryKeys.publicationStatus(projectId!),
    queryFn: () => getPublicationStatus(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })
}

export function usePublishProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      acknowledgeExcludedCanvases = false,
    }: {
      projectId: string
      acknowledgeExcludedCanvases?: boolean
    }) => publishProject(projectId, acknowledgeExcludedCanvases),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.publicationStatus(projectId) })
    },
  })
}

export function useUnpublishProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => unpublishProject(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.publicationStatus(projectId) })
    },
  })
}

export function usePublishedSnapshot(projectToken: string | undefined, versionSlug: string | undefined) {
  return useQuery<PublishedSnapshot>({
    queryKey: queryKeys.publishedSnapshot(`${projectToken}/${versionSlug}`),
    queryFn: () => getPublishedSnapshot(projectToken!, versionSlug!),
    enabled: !!projectToken && !!versionSlug,
  })
}

export function usePublicationVersions(projectId: string | undefined) {
  return useQuery<VersionListItem[]>({
    queryKey: queryKeys.publicationVersions(projectId!),
    queryFn: () => getPublicationVersions(projectId!),
    enabled: !!projectId,
  })
}

export function usePublicationVersion(projectId: string | undefined, versionNumber: number | undefined) {
  return useQuery<VersionDetail>({
    queryKey: queryKeys.publicationVersion(projectId!, versionNumber!),
    queryFn: () => getPublicationVersion(projectId!, versionNumber!),
    enabled: !!projectId && versionNumber !== undefined,
  })
}

export function usePublicationLive(projectId: string | undefined) {
  return useQuery<LiveSnapshot>({
    queryKey: queryKeys.publicationLive(projectId!),
    queryFn: () => getPublicationLive(projectId!),
    enabled: !!projectId,
  })
}

export function useClonePublishedProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectToken, versionSlug }: { projectToken: string; versionSlug: string }) =>
      clonePublishedProject(projectToken, versionSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
    },
  })
}

// Public templates catalog (RFC-0934 P1 gallery). Public/unauthenticated read;
// low-churn (frozen publications) so a generous staleTime avoids refetch churn.
export function useTemplates(limit = 50, offset = 0) {
  return useQuery<TemplateCatalogResponse>({
    queryKey: queryKeys.templates(limit, offset),
    queryFn: () => getTemplates(limit, offset),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  })
}

// ---------------------------------------------------------------------------
// Style Library hooks (replaces useStyleLibrary + useUserStyles)
// ---------------------------------------------------------------------------

/**
 * Map the NO_STYLE sentinel plus hardcoded VISUAL_STYLES to placeholder data.
 */
function fallbackStyles(): StyleSummary[] {
  return [NO_STYLE_OPTION, ...VISUAL_STYLES].map((s) => ({
    id: s.value,
    name: s.label,
    prompt: '',
    use_reference_in_generation: false,
    reference_image_count: 0,
    tags: [...s.tags],
    thumbnail_url: null,
    preview_image_urls: [],
    is_static_fallback: true,
  }))
}

export { fallbackStyles }

/**
 * Fetch public styles from the API via React Query.
 * Replaces the hand-rolled useStyleLibrary hook.
 */
export function useStyles(enabled = true) {
  const { i18n } = useTranslation()
  const uiLocale = normalizeLocale(i18n.language) ?? 'en'
  return useQuery<StyleListResponse>({
    queryKey: queryKeys.styles(uiLocale),
    queryFn: async () => {
      styleLog('fetching public styles')
      const result = await listStyles()
      styleLog('public styles fetched, count=%d', result.styles.length)
      return result
    },
    enabled,
    staleTime: 5 * 60_000, // public styles rarely change
    placeholderData: { styles: fallbackStyles() },
  })
}

/**
 * Fetch current user's custom styles via React Query.
 * Replaces the hand-rolled useUserStyles hook.
 */
export function useUserStylesQuery(enabled = true) {
  const auth = useAuthOptional()
  return useQuery<UserStyleListResponse>({
    queryKey: queryKeys.userStyles,
    queryFn: async () => {
      styleLog('fetching user styles')
      const result = await listUserStyles()
      styleLog('user styles fetched, count=%d', result.styles.length)
      return result
    },
    enabled: enabled && !!auth?.isAuthenticated,
    staleTime: 60_000,
  })
}

export function useStyleDetail(styleId: string | null | undefined, enabled = true) {
  const { i18n } = useTranslation()
  const uiLocale = normalizeLocale(i18n.language) ?? 'en'
  return useQuery<StyleDetail>({
    queryKey: queryKeys.style(styleId ?? '', uiLocale),
    queryFn: async () => {
      styleLog('fetching public style detail, styleId=%s', styleId)
      return getStyle(styleId!)
    },
    enabled: enabled && !!styleId,
    staleTime: 5 * 60_000,
  })
}

export function useUserStyleDetail(styleId: string | null | undefined, enabled = true) {
  const { isAuthenticated } = useAuth()
  return useQuery<StyleDetail>({
    queryKey: queryKeys.userStyle(styleId ?? ''),
    queryFn: async () => {
      styleLog('fetching user style detail, styleId=%s', styleId)
      return getUserStyle(styleId!)
    },
    enabled: enabled && isAuthenticated && !!styleId,
    staleTime: 5 * 60_000,
  })
}

// -- Favourites (Workplace post 1556548202769144) --

/**
 * Fetch the current user's favourite style refs (most-recent first). Gated on
 * auth — anonymous landing users have no favourites. Cache is cleared globally
 * on sign-out via AuthContext's invalidateQueries().
 */
export function useFavoriteStyles(enabled = true) {
  const auth = useAuthOptional()
  return useQuery<FavoritesListResponse>({
    queryKey: queryKeys.styleFavorites,
    queryFn: async () => {
      styleLog('fetching favourites')
      const result = await listFavorites()
      styleLog('favourites fetched, count=%d', result.favorites.length)
      return result
    },
    enabled: enabled && !!auth?.isAuthenticated,
    staleTime: 60_000,
  })
}

function _favoritesWith(list: FavoriteRef[], ref: FavoriteRef): FavoriteRef[] {
  // Prepend (most-recent-first, matching the backend order); no-op if present.
  if (list.some((f) => favoriteKey(f) === favoriteKey(ref))) return list
  return [ref, ...list]
}

function _favoritesWithout(list: FavoriteRef[], ref: FavoriteRef): FavoriteRef[] {
  return list.filter((f) => favoriteKey(f) !== favoriteKey(ref))
}

/**
 * Toggle a favourite with a PER-ENTRY optimistic update. Unlike a whole-family
 * snapshot/restore, onError reverts ONLY this ref, so a failed toggle can't
 * clobber a concurrent successful toggle of a different style.
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ref, next }: { ref: FavoriteRef; next: boolean }) =>
      next ? addFavorite(ref) : removeFavorite(ref),
    onMutate: async ({ ref, next }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.styleFavorites })
      queryClient.setQueryData<FavoritesListResponse>(queryKeys.styleFavorites, (old) => {
        const list = old?.favorites ?? []
        return { favorites: next ? _favoritesWith(list, ref) : _favoritesWithout(list, ref) }
      })
      return { ref, next }
    },
    onError: (_err, { ref, next }) => {
      // Revert only this ref (undo the optimistic op) — never a blanket restore.
      queryClient.setQueryData<FavoritesListResponse>(queryKeys.styleFavorites, (old) => {
        const list = old?.favorites ?? []
        return { favorites: next ? _favoritesWithout(list, ref) : _favoritesWith(list, ref) }
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.styleFavorites })
    },
  })
}

// ---------------------------------------------------------------------------
// Dashboard Tab hooks
// ---------------------------------------------------------------------------

/**
 * The tab scope the dashboard is currently showing.
 *
 * Tabs are stored per (team, visibility). The team comes from the same
 * resolver the project list uses, so both settle together; the visibility is
 * read from the URL, which is where the Private/Team switch keeps it. Reading
 * it here rather than threading it through props keeps every tab mutation on
 * the same scope as the list it is mutating -- a create that landed in a
 * different scope from the one on screen would look like it silently failed.
 *
 * `ready` is false until the team resolves. Firing before then sends an
 * unscoped request, which the server answers with the pre-teams single tab
 * set -- the cross-scope bleed this whole change removes.
 */
export function useTabScope(): { scope: TabScope; ready: boolean } {
  const { teamId, ready } = useProjectListTeamScope()
  const [searchParams] = useSearchParams()
  const visibility = asVisibility(searchParams.get('visibility'))
  return { scope: { teamId, visibility }, ready }
}

export function useDashboardTabs() {
  const { scope, ready } = useTabScope()
  return useQuery<{ tabs: DashboardTab[] }>({
    // Scope is part of the KEY, not just the request: without it react-query
    // hands the previous scope's tabs to the new one and the bar appears not
    // to change on switch.
    queryKey: queryKeys.dashboardTabsScoped(scope),
    queryFn: () => api.listTabs(scope),
    enabled: ready,
    staleTime: 30_000,
  })
}

// Paginated projects for one tab. `tabId` is either a real tab UUID or the
// "all" sentinel. `page` is 1-indexed; offset = (page-1) * DASHBOARD_PAGE_SIZE.
// `sortBy`/`sortDirection` are honored server-side only for the "all" tab —
// named tabs use their persisted sort_by/sort_direction (set via PATCH).
//
// `namePrefix` is a case-insensitive SUBSTRING (contains) match on project.name
// (the identifier keeps the historical name for continuity with the retained
// `name_prefix` wire param). Pass the already-trimmed, lowercased query from the
// caller — the hook treats empty values as "no filter" and includes it in the
// query key so React Query refetches whenever the search input changes.
export function useDashboardTabProjects(
  tabId: string,
  page: number,
  sortBy?: TabSortBy,
  sortDirection?: TabSortDirection,
  namePrefix?: string,
  // RFC-1371 D3. In the KEY as well as the request: the two halves are
  // different result sets, so without it react-query would serve the private
  // page after a flip to team and the switch would appear to do nothing —
  // exactly the bug the team id had.
  visibility?: string,
) {
  const offset = (page - 1) * DASHBOARD_PAGE_SIZE
  const prefixKey = namePrefix && namePrefix.trim() ? namePrefix : null
  // The active team is part of the KEY, not just the request. Sending it alone
  // would leave react-query serving the previous team's cached page on switch,
  // so the dashboard would still appear not to change.
  //
  // `ready` holds the FIRST load until the flag snapshot and the team have both
  // settled — otherwise each settlement is its own key and the dashboard issues
  // two GETs, the first of them unscoped. See useProjectListTeamScope.
  const { teamId, ready } = useProjectListTeamScope()
  return useQuery<PaginatedResponse<ProjectListItem>>({
    enabled: ready,
    queryKey: [
      ...queryKeys.dashboardTabProjects(tabId, page),
      sortBy ?? null,
      sortDirection ?? null,
      prefixKey,
      teamId,
      visibility ?? null,
    ],
    queryFn: () =>
      api.listTabProjects(tabId, {
        limit: DASHBOARD_PAGE_SIZE,
        offset,
        sortBy,
        sortDirection,
        namePrefix: prefixKey ?? undefined,
        teamId: teamId ?? undefined,
        visibility,
      }),
    // Keep the previous page's data on screen while a new query (sort change,
    // page change, or prefix-search refetch) is in flight. Without this, every
    // queryKey change wipes `data` to undefined → HomePage falls into its
    // loading branch → the search input + tab bar unmount → input loses
    // focus mid-type. With `placeholderData: keepPreviousData`, the list
    // swaps from old → new results in place with no skeleton flash.
    placeholderData: keepPreviousData,
    // Quick navigation between pages shouldn't refetch the whole world;
    // freshness matters more than caching here because newly-created
    // projects should appear without a manual reload.
    staleTime: 5_000,
  })
}

/**
 * Compact owner-only project read for the signed-in landing shelf. Unlike the
 * dashboard, this opts out of starred-first ordering so "recent" is strict
 * `updated_at DESC`.
 */
export function useRecentProjects(enabled: boolean) {
  // TEAM-SCOPED, like every other project list. Without this the shelf sent no
  // `team_id` at all, so the backend skipped scoping entirely and "Recent"
  // showed work from whichever teams the user happens to belong to — including
  // projects that are not in the team they currently have selected. A shelf
  // that ignores the switcher is worse than one that is merely stale: it
  // contradicts the rest of the page.
  //
  // `visibility` is 'any': both halves of the ACTIVE TEAM. An earlier version
  // sent 'private', copying the dashboard's default, and that hid every
  // published project from the shelf -- reported as "the project I just opened
  // in my team is not in Recent". The dashboard has a switch to express intent;
  // the shelf has none, so filtering one half out is a guess, and the wrong one.
  const { teamId, ready } = useProjectListTeamScope()
  return useQuery<PaginatedResponse<ProjectListItem>>({
    queryKey: [
      ...queryKeys.dashboardTabProjects('all', 1),
      'landing-recent',
      LANDING_RECENT_PROJECT_LIMIT,
      teamId,
      RECENT_SHELF_VISIBILITY,
    ],
    queryFn: () =>
      api.listTabProjects('all', {
        limit: LANDING_RECENT_PROJECT_LIMIT,
        offset: 0,
        sortBy: 'updated_at',
        sortDirection: 'desc',
        starredFirst: false,
        includeResumeAction: true,
        teamId: teamId ?? undefined,
        visibility: RECENT_SHELF_VISIBILITY,
      }),
    // Held until the team scope settles, for the same reason the dashboard is:
    // firing before it resolves sends an UNSCOPED request whose answer is the
    // cross-team list this fixes.
    enabled: enabled && ready,
    staleTime: 30_000,
  })
}

export function useCreateTab() {
  const queryClient = useQueryClient()
  const { scope } = useTabScope()
  const key = queryKeys.dashboardTabsScoped(scope)

  return useMutation({
    mutationFn: (name: string) => api.createTab(name, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export function useUpdateTab() {
  const queryClient = useQueryClient()
  const { scope } = useTabScope()
  const key = queryKeys.dashboardTabsScoped(scope)

  return useMutation({
    mutationFn: ({
      tabId,
      data,
    }: {
      tabId: string
      data: { name?: string; sort_by?: TabSortBy; sort_direction?: TabSortDirection }
    }) => api.updateTab(tabId, data, scope),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(key)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(key, (old) => {
        if (!old) return old
        return {
          tabs: old.tabs.map((t) =>
            t.id === variables.tabId ? { ...t, ...variables.data } : t,
          ),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export function useDeleteTab() {
  const queryClient = useQueryClient()
  const { scope } = useTabScope()
  const key = queryKeys.dashboardTabsScoped(scope)

  return useMutation({
    mutationFn: (tabId: string) => api.deleteTab(tabId, scope),
    onMutate: async (tabId) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(key)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(key, (old) => {
        if (!old) return old
        return { tabs: old.tabs.filter((t) => t.id !== tabId) }
      })
      return { previous }
    },
    onError: (_err, _tabId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export function useReorderTabs() {
  const queryClient = useQueryClient()
  const { scope } = useTabScope()
  const key = queryKeys.dashboardTabsScoped(scope)

  return useMutation({
    mutationFn: (tabIds: string[]) => api.reorderTabs(tabIds, scope),
    onMutate: async (tabIds) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(key)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(key, (old) => {
        if (!old) return old
        const tabMap = new Map(old.tabs.map((t) => [t.id, t]))
        const reordered = tabIds
          .map((id, i) => {
            const tab = tabMap.get(id)
            return tab ? { ...tab, position: i } : undefined
          })
          .filter((t): t is DashboardTab => t !== undefined)
        return { tabs: reordered }
      })
      return { previous }
    },
    onError: (_err, _ids, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export function useAssignProject() {
  const queryClient = useQueryClient()
  const { scope } = useTabScope()
  const key = queryKeys.dashboardTabsScoped(scope)

  return useMutation({
    mutationFn: ({ tabId, projectId }: { tabId: string; projectId: string }) =>
      api.assignProjectToTab(tabId, projectId, scope),
    onMutate: async ({ tabId, projectId }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(key)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(key, (old) => {
        if (!old) return old
        return {
          tabs: old.tabs.map((t) => {
            // Remove from old tab
            const filtered = t.project_ids.filter((pid) => pid !== projectId)
            // Add to new tab
            if (t.id === tabId) {
              return { ...t, project_ids: [...filtered, projectId] }
            }
            return { ...t, project_ids: filtered }
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
    },
  })
}

export function useUnassignProject() {
  const queryClient = useQueryClient()
  const { scope } = useTabScope()
  const key = queryKeys.dashboardTabsScoped(scope)

  return useMutation({
    mutationFn: ({ tabId, projectId }: { tabId: string; projectId: string }) =>
      api.unassignProjectFromTab(tabId, projectId, scope),
    onMutate: async ({ tabId, projectId }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(key)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(key, (old) => {
        if (!old) return old
        return {
          tabs: old.tabs.map((t) => {
            if (t.id === tabId) {
              return { ...t, project_ids: t.project_ids.filter((pid) => pid !== projectId) }
            }
            return t
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
    },
  })
}

export function useReorderProjects() {
  const queryClient = useQueryClient()
  const { scope } = useTabScope()
  const key = queryKeys.dashboardTabsScoped(scope)

  return useMutation({
    mutationFn: ({
      tabId,
      projectIds,
      offset = 0,
    }: {
      tabId: string
      projectIds: string[]
      offset?: number
    }) => api.reorderProjects(tabId, projectIds, offset, scope),
    onMutate: async ({ tabId, projectIds, offset = 0 }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(key)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(key, (old) => {
        if (!old) return old
        const manualSortBy: TabSortBy = 'manual'
        return {
          tabs: old.tabs.map((t) => {
            if (t.id !== tabId) return t
            const ids = [...t.project_ids]
            // Splice the visible window in place — the optimistic update has
            // to match the server's splice semantics or the cache diverges.
            ids.splice(offset, projectIds.length, ...projectIds)
            return { ...t, project_ids: ids, sort_by: manualSortBy }
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous)
      }
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: ['dashboardTabProjects'] })
    },
    onSuccess: () => {
      // Don't invalidate on success — optimistic data is already correct.
      // Refetching would cause a flash as dnd-kit resets CSS transforms
      // when React Query replaces the cache with new object references.
    },
  })
}


// ---------------------------------------------------------------------------
// White-glove onboarding (/admin/onboarding)
//
// Every mutation returns the FULL run, so each one seeds the run query
// directly rather than invalidating it. That matters here: a step mutation is
// the only authoritative report of what just happened to the money or the
// mail, and a refetch could race another operator's write and show a state
// that never corresponded to this operator's action.
// ---------------------------------------------------------------------------

export const onboardingRunKey = (runId: string, targetIds?: string[]): string[] => targetIds === undefined
  ? ['onboarding-run', runId]
  : ['onboarding-scoped-run', runId, JSON.stringify(normalizeOnboardingScope(targetIds))]
export const onboardingPreflightKey = (runId: string, targetIds?: string[]): string[] => targetIds === undefined
  ? ['onboarding-preflight', runId]
  : ['onboarding-scoped-preflight', runId, JSON.stringify(normalizeOnboardingScope(targetIds))]
const responseRunKey = (run: OnboardingRun) => onboardingRunKey(run.run_id, run.scope === 'targets' ? run.scope_target_ids ?? run.targets.map((target) => target.target_id) : undefined)

export const onboardingEmailPreviewKey = (runId: string, targetId: string): string[] => [
  'onboarding-email-preview',
  runId,
  targetId,
]
export const onboardingCapabilitiesKey: string[] = ['onboarding-capabilities']
export const onboardingEmailTemplateKey = (runId: string): string[] => [
  'onboarding-email-template',
  runId,
]

export function useOnboardingRun(runId: string | null, targetIds?: string[]) {
  return useQuery({
    queryKey: onboardingRunKey(runId ?? '', targetIds),
    queryFn: () => {
      // `enabled` below already prevents this, but narrowing honestly beats
      // casting: if the guard is ever removed the failure is loud.
      if (!runId) throw new Error('useOnboardingRun called without a run id')
      return targetIds === undefined ? api.getOnboardingRun(runId) : api.getOnboardingRun(runId, targetIds)
    },
    enabled: Boolean(runId),
    // No polling. Steps only advance when this operator clicks, so background
    // refetching would add load without ever showing something new.
    refetchOnWindowFocus: false,
  })
}

export function useOnboardingPreflight(runId: string | null, targetIds?: string[]) {
  return useQuery({
    queryKey: onboardingPreflightKey(runId ?? '', targetIds),
    queryFn: () => {
      if (!runId) throw new Error('useOnboardingPreflight called without a run id')
      return targetIds === undefined ? api.getOnboardingPreflight(runId) : api.getOnboardingPreflight(runId, targetIds)
    },
    enabled: Boolean(runId),
    // Read-only on the server, but it reflects live environment state (the
    // registration trigger, the account, the balance), so it is re-read rather
    // than cached across a session.
    staleTime: 0,
    refetchOnWindowFocus: false,
  })
}

/**
 * The welcome email as the partner would get it, placeholder password and all.
 *
 * A plain read: the server claims no step for it, so fetching it can never
 * close or advance anything. Only enabled once the email step is actually the
 * next thing to do, so the operator reviews the copy at the moment it matters.
 */
export function useOnboardingEmailPreview(
  runId: string | null,
  targetId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: onboardingEmailPreviewKey(runId ?? '', targetId ?? ''),
    queryFn: () => {
      if (!runId || !targetId) {
        throw new Error('useOnboardingEmailPreview called without a run and target')
      }
      return api.getOnboardingEmailPreview(runId, targetId)
    },
    enabled: enabled && Boolean(runId) && Boolean(targetId),
    // Re-rendered on each visit rather than cached: the warnings reflect live
    // SMTP configuration, and the preview is cheap.
    staleTime: 0,
    refetchOnWindowFocus: false,
  })
}

/** Mails the placeholder version to the signed-in operator. Touches no step. */
export function useSendOnboardingTestEmail(targetIds?: string[]) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId, targetId }: { runId: string; targetId: string }) =>
      api.sendOnboardingTestEmail(runId, targetId),
    // The server records the attestation; refetch so the send unlocks from it.
    onSuccess: (_result, { runId }) =>
      queryClient.invalidateQueries({ queryKey: onboardingRunKey(runId, targetIds) }),
  })
}

/** Whether onboarding and custom templates are switched on here (P3). */
export function useOnboardingCapabilities() {
  return useQuery({
    queryKey: onboardingCapabilitiesKey,
    queryFn: () => api.getOnboardingCapabilities(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/** The run's email template source: its custom version, or the default to start from. */
export function useOnboardingEmailTemplate(runId: string | null, enabled = true) {
  return useQuery({
    queryKey: onboardingEmailTemplateKey(runId ?? ''),
    queryFn: () => {
      if (!runId) throw new Error('useOnboardingEmailTemplate called without a run id')
      return api.getOnboardingEmailTemplate(runId)
    },
    enabled: enabled && Boolean(runId),
    refetchOnWindowFocus: false,
  })
}

/** Lint and render an unsaved draft. Stores nothing, so it is a mutation only for its body. */
export function usePreviewOnboardingEmailTemplate() {
  return useMutation({
    mutationFn: ({
      runId,
      draft,
      targetId,
    }: {
      runId: string
      draft: OnboardingEmailTemplateDraft
      targetId: string | null
    }) => api.previewOnboardingEmailTemplate(runId, draft, targetId),
  })
}

/**
 * After a template save or reset: the server cleared the run's test-send
 * attestation and every preview now renders different copy, so all three are
 * refetched rather than patched.
 */
function refreshAfterTemplateChange(
  queryClient: ReturnType<typeof useQueryClient>,
  runId: string,
  template: OnboardingEmailTemplate | null,
): void {
  if (template) queryClient.setQueryData(onboardingEmailTemplateKey(runId), template)
  else void queryClient.invalidateQueries({ queryKey: onboardingEmailTemplateKey(runId) })
  void queryClient.invalidateQueries({ queryKey: onboardingRunKey(runId) })
  void queryClient.invalidateQueries({ queryKey: ['onboarding-email-preview', runId] })
}

export function useSaveOnboardingEmailTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId, draft }: { runId: string; draft: OnboardingEmailTemplateDraft }) =>
      api.saveOnboardingEmailTemplate(runId, draft),
    onSuccess: (template, { runId }) => refreshAfterTemplateChange(queryClient, runId, template),
    // A 409 TEMPLATE_FROZEN means our cached "editable" is stale (an email was
    // claimed meanwhile): refetch so the editor turns read-only with the reason.
    onError: (_error, { runId }) => refreshAfterTemplateChange(queryClient, runId, null),
  })
}

export function useResetOnboardingEmailTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId }: { runId: string }) => api.resetOnboardingEmailTemplate(runId),
    onSuccess: (template, { runId }) => refreshAfterTemplateChange(queryClient, runId, template),
    onError: (_error, { runId }) => refreshAfterTemplateChange(queryClient, runId, null),
  })
}

/** Intake responses only seed the exact new run scope; old scopes are fetched separately. */
export function useCreateOnboardingIntake() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: import('./onboardingTypes').OnboardingIntakeRequest) => api.createOnboardingIntake(request),
    onSuccess: (result, request) => {
      if (request.validateOnly || !result.run) return
      const scope = result.scopes.find((candidate) => candidate.run_id === result.run?.run_id)
      if (!scope) throw new Error('Intake response is missing its new run scope')
      const run = validateScopedOnboardingRun(result.run, scope.target_ids)
      queryClient.setQueryData(onboardingRunKey(run.run_id, scope.target_ids), validateScopedOnboardingRun(run, scope.target_ids))
    },
  })
}

export function useCreateOnboardingRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      reason,
      targets,
      allowRepeatRecipients = false,
      acknowledgedPriorTargets = {},
    }: {
      reason: string
      targets: OnboardingRunTargetInput[]
      acknowledgedPriorTargets?: Record<string, string>
      allowRepeatRecipients?: boolean
    }) => api.createOnboardingRun(reason, targets, allowRepeatRecipients, acknowledgedPriorTargets),
    onSuccess: (run) => {
      queryClient.setQueryData(responseRunKey(run), run)
    },
  })
}

/**
 * An awaitable, authoritative re-read of the run, written into the cache. The
 * bulk loop calls it at batch start, on any conflict and at batch end (B3/B4),
 * and must classify from what it RETURNS, not from a possibly older render.
 */
export function useRefreshOnboardingRun(runId: string | null, targetIds?: string[]) {
  const queryClient = useQueryClient()
  return async (): Promise<OnboardingRun> => {
    if (!runId) throw new Error('useRefreshOnboardingRun called without a run id')
    const fresh = targetIds === undefined ? await api.getOnboardingRun(runId) : await api.getOnboardingRun(runId, targetIds)
    queryClient.setQueryData(onboardingRunKey(runId, targetIds), fresh)
    return fresh
  }
}

/** Same, for preflight: the bulk loop derives blocked rows from a fresh read. */
export function useRefreshOnboardingPreflight(runId: string | null, targetIds?: string[]) {
  const queryClient = useQueryClient()
  return async (): Promise<OnboardingPreflight> => {
    if (!runId) throw new Error('useRefreshOnboardingPreflight called without a run id')
    const fresh = targetIds === undefined ? await api.getOnboardingPreflight(runId) : await api.getOnboardingPreflight(runId, targetIds)
    queryClient.setQueryData(onboardingPreflightKey(runId, targetIds), fresh)
    return fresh
  }
}

/**
 * Validate or create a bulk run from CSV (plan B1). Validation never creates;
 * a created run is seeded into the cache like the single-target create.
 */
export function useCreateOnboardingCsvRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: OnboardingCsvRequest) => api.createOnboardingCsvRun(request),
    onSuccess: (result) => {
      if (result.run) queryClient.setQueryData(responseRunKey(result.run), result.run)
    },
  })
}

/**
 * Advance one step for one target and patch ONLY that target into the cached
 * run (plan B3). A separate hook from useAdvanceOnboardingStep so the P1
 * whole-run path is untouched. If an older server ignores `?response=target`
 * and returns the whole run, only unscoped callers accept that fallback.
 */
export function useAdvanceOnboardingTarget(targetIds?: string[]) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      runId,
      targetId,
      step,
      options,
    }: {
      runId: string
      targetId: string
      step: OnboardingStepName
      options?: OnboardingAdvanceOptions
    }) => targetIds === undefined ? api.advanceOnboardingTarget(runId, targetId, step, options) : api.advanceOnboardingTarget(runId, targetId, step, options, targetIds),
    onSuccess: (result, { runId }) => {
      if (isTargetResponse(result)) {
        queryClient.setQueryData<OnboardingRun>(onboardingRunKey(runId, targetIds), (run) =>
          run ? withTarget(run, result) : run,
        )
      } else {
        if (targetIds !== undefined) throw new Error('Scoped advance requires a target response')
        queryClient.setQueryData(onboardingRunKey(runId, targetIds), result)
      }
    },
    // The bulk loop refetches the whole run on any failure; see runBatch.
  })
}

/** Operator recovery of an interrupted step. Refused unless its executor is gone. */
export function useRecoverOnboardingStep(targetIds?: string[]) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      runId,
      targetId,
      step,
    }: {
      runId: string
      targetId: string
      step: OnboardingStepName
    }) => targetIds === undefined ? api.recoverOnboardingStep(runId, targetId, step) : api.recoverOnboardingStep(runId, targetId, step, targetIds),
    onSuccess: (run) => {
      queryClient.setQueryData(onboardingRunKey(run.run_id, targetIds), run)
    },
    // A refusal still means the server looked at fresh state; show it.
    onError: (_error, { runId }) => {
      void queryClient.invalidateQueries({ queryKey: onboardingRunKey(runId, targetIds) })
    },
  })
}

export function useAdvanceOnboardingStep(targetIds?: string[]) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      runId,
      targetId,
      step,
      options,
    }: {
      runId: string
      targetId: string
      step: OnboardingStepName
      options?: OnboardingAdvanceOptions
    }) => targetIds === undefined ? api.advanceOnboardingStep(runId, targetId, step, options) : api.advanceOnboardingStep(runId, targetId, step, options, targetIds),
    onSuccess: (run) => {
      queryClient.setQueryData(onboardingRunKey(run.run_id, targetIds), run)
      // Preflight reads live environment state that a step may have changed —
      // the allowlist row, the account, the balance — so it is dropped rather
      // than kept and shown stale.
      queryClient.invalidateQueries({ queryKey: onboardingPreflightKey(run.run_id, targetIds) })
    },
    // A FAILED advance still changed the server. `_run_step` raising
    // OnboardingEffectError makes the route call `record_failure` and COMMIT
    // before it returns the error, so the step is now 'failed' with the
    // provider's own reason on the row. Only onSuccess carried a run payload,
    // so the page kept rendering the pre-call status and that reason — the
    // thing CLAUDE.md #7 exists to surface — was never shown. Nothing else
    // heals it either: this query has no polling and refetchOnWindowFocus is
    // off, so the stale status survives until a reload.
    //
    // A failed step may also have partially succeeded (allowlist written,
    // account created), so refetch rather than patch the cache from here.
    onError: (_error, { runId }) => {
      void queryClient.invalidateQueries({ queryKey: onboardingRunKey(runId, targetIds) })
      void queryClient.invalidateQueries({ queryKey: onboardingPreflightKey(runId, targetIds) })
    },
  })
}

export function useSettleOnboardingStep(targetIds?: string[]) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      runId,
      targetId,
      step,
      outcome,
    }: {
      runId: string
      targetId: string
      step: OnboardingStepName
      outcome: OnboardingSettleOutcome
    }) => targetIds === undefined ? api.settleOnboardingStep(runId, targetId, step, outcome) : api.settleOnboardingStep(runId, targetId, step, outcome, targetIds),
    onSuccess: (run) => {
      queryClient.setQueryData(onboardingRunKey(run.run_id, targetIds), run)
    },
    // NOT_SETTLEABLE means the step is no longer 'delivery_unknown' — i.e.
    // someone else already settled it, so our cached status is the stale one.
    // Refetching replaces the buttons with the real state instead of leaving
    // an operator clicking a prompt that cannot apply any more.
    onError: (_error, { runId }) => {
      void queryClient.invalidateQueries({ queryKey: onboardingRunKey(runId, targetIds) })
    },
  })
}

export function useCancelOnboardingRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ runId }: { runId: string }) => api.cancelOnboardingRun(runId),
    onSuccess: (run) => {
      queryClient.setQueryData(responseRunKey(run), run)
    },
    // Cancel commits before it responds, so a transport failure leaves us
    // genuinely unsure whether the run was cancelled. Refetch rather than
    // assume it was not — reading "running" on a cancelled run would invite
    // an operator to keep clicking steps the claim predicate will reject.
    onError: (_error, { runId }) => {
      void queryClient.invalidateQueries({ queryKey: onboardingRunKey(runId) })
    },
  })
}

export function useOnboardingOrgs(enabled = true) {
  return useQuery({
    queryKey: ['onboarding-orgs'],
    queryFn: () => api.listOnboardingOrgs(),
    enabled,
  })
}

/** Teams `email` owns or administers. Fetched only for a complete address. */
export function useOnboardingTeamLookup(email: string | null) {
  return useQuery({
    queryKey: ['onboarding-team-lookup', email ?? ''],
    queryFn: () => {
      if (!email) throw new Error('useOnboardingTeamLookup called without an email')
      return api.lookupOnboardingTeams(email)
    },
    enabled: Boolean(email),
  })
}

export function useOnboardingOrgRoster(slug: string | null) {
  return useQuery({
    queryKey: ['onboarding-org-roster', slug ?? ''],
    queryFn: () => {
      if (!slug) throw new Error('useOnboardingOrgRoster called without a slug')
      return api.getOnboardingOrgRoster(slug)
    },
    // Only fetched when the operator is actually deciding an assignment: the
    // roster is what tells them whether a slug's name matches who is in it.
    enabled: Boolean(slug),
  })
}
